import { createBigDecimal } from '@0xtorch/big-decimal'
import type { Transfer } from '@0xtorch/core'
import type { z } from 'zod'
import type { Chain } from '../../../../chain'
import type {
  InternalTransaction,
  LowerHex,
  TransactionDecoded,
} from '../../../../types'
import { createEvmAddressId } from '../../../../utils'
import { getAllValue } from './allValue'
import { checkValueIsArrayIn } from './arrayIn'
import { checkValueIsBetweenBigInt } from './betweenBigInt'
import type { internalTransferGeneratorSchema } from './schema'

export const generateTransfersByInternalTransactions = ({
  schema,
  chain,
  transaction,
  relatedAddresses,
}: {
  readonly schema: z.infer<typeof internalTransferGeneratorSchema>
  readonly chain: Chain
  readonly transaction: TransactionDecoded
  readonly relatedAddresses: Set<LowerHex>
}): readonly Transfer[] => {
  const transfers: Transfer[] = []
  let currentIndex = -1
  for (const internalTransaction of transaction.internalTransactions) {
    const matched = checkInternalTransactionIsMatchPattern({
      internalTransaction,
      schema,
      transaction,
      relatedAddresses,
    })
    if (matched) {
      currentIndex += 1
      if (schema.index === undefined || currentIndex === schema.index) {
        transfers.push({
          direction: schema.direction,
          from: createEvmAddressId({
            chainId: transaction.chainId,
            address: internalTransaction.from,
          }),
          to:
            internalTransaction.to === undefined
              ? undefined
              : createEvmAddressId({
                  chainId: transaction.chainId,
                  address: internalTransaction.to,
                }),
          asset: chain.nativeToken.currency,
          amount: createBigDecimal(
            internalTransaction.value,
            chain.nativeToken.decimals,
          ),
        })
      }
      if (currentIndex === schema.index) {
        return transfers
      }
    }
  }
  if (schema.index === undefined) {
    return transfers
  }
  throw new Error('Matched internal transaction is not found')
}

const checkInternalTransactionIsMatchPattern = ({
  internalTransaction,
  schema,
  transaction,
  relatedAddresses,
}: {
  readonly internalTransaction: InternalTransaction
  readonly schema: z.infer<typeof internalTransferGeneratorSchema>
  readonly transaction: TransactionDecoded
  readonly relatedAddresses: Set<LowerHex>
}): boolean => {
  // from
  if (
    schema.from !== undefined &&
    !checkValueIsArrayIn({
      value: internalTransaction.from,
      schema: schema.from,
      transaction,
      relatedAddresses,
    })
  ) {
    return false
  }

  // to
  if (
    schema.to !== undefined &&
    !checkValueIsArrayIn({
      value: internalTransaction.to ?? '',
      schema: schema.to,
      transaction,
      relatedAddresses,
    })
  ) {
    return false
  }

  // value
  if (schema.value !== undefined) {
    if (typeof schema.value === 'string') {
      if (internalTransaction.value.toString() !== schema.value) {
        return false
      }
    } else {
      switch (schema.value.type) {
        case 'between': {
          if (
            !checkValueIsBetweenBigInt({
              value: internalTransaction.value,
              schema: schema.value,
            })
          ) {
            return false
          }
          break
        }
        case 'function':
        case 'value':
        case 'join':
        case 'slice':
        case 'math':
        case 'chain-id':
        case 'tx-hash':
        case 'from':
        case 'to':
        case 'log':
        case 'erc20':
        case 'erc721':
        case 'erc1155': {
          const value = getAllValue({
            schema: schema.value,
            transaction,
            relatedAddresses,
          })
          if (internalTransaction.value.toString() !== value) {
            return false
          }
          break
        }
        default: {
          throw new Error(`Unknown value type: ${schema.value satisfies never}`)
        }
      }
    }
  }

  return true
}
